import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { Field, FormGrid, TextInput } from '../../components/ui';
import { dateLabel } from '../../lib/format';
import type { CompanyProfile } from '../../lib/types';

// 公司财税抬头字段（对齐后端 COMPANY_PROFILE_FIELDS）：key → { 中文, 西语/说明, 类型 }
type FieldKind = 'text' | 'area' | 'date';
interface CompanyField { key: keyof CompanyProfile; label: string; hint?: string; kind?: FieldKind; span?: boolean; }

// 可编辑的公司资料字段（法定名称必填；CAI/销售点/打印授权属 ARCA 法定授权，务必由会计师确认后录入真实值，禁止臆造）。
const COMPANY_FIELDS: CompanyField[] = [
  { key: 'legal_name', label: '公司法定名称 Razón social', hint: '正式抬头必填 · 打印始终以文本保留', span: true },
  { key: 'trade_name', label: '商业名称 / 品牌' },
  { key: 'cuit', label: 'C.U.I.T.' },
  { key: 'iibb', label: 'Ingresos Brutos（gross income）' },
  { key: 'vat_condition', label: 'IVA 身份 Condición frente al IVA' },
  { key: 'inicio_actividad', label: '营业开始日期 Inicio de actividades' },
  { key: 'commercial_address', label: '商业/营业地址 Domicilio comercial', span: true },
  { key: 'office_address', label: '办公地址', span: true },
  { key: 'city', label: '城市' },
  { key: 'province', label: '省份' },
  { key: 'postal_code', label: '邮编' },
  { key: 'country', label: '国家' },
  { key: 'phone', label: '电话' },
  { key: 'email', label: '邮箱' },
  { key: 'website', label: '网址' },
  { key: 'logo_url', label: 'Logo 图片 URL（可选）', hint: '不配置则打印回退文本品牌，法定名称与税务数据始终以文本保留', span: true },
];

// 正式 Remito R 所需的“公司侧”法定字段（与信息检查，逐条对齐后端 remitoReadiness 的 company 来源）。
const FISCAL_CHECKLIST: Array<{ key: keyof CompanyProfile; label: string; es: string }> = [
  { key: 'legal_name', label: '公司法定名称', es: 'Razón social del emisor' },
  { key: 'cuit', label: '公司 CUIT', es: 'C.U.I.T. del emisor' },
  { key: 'commercial_address', label: '公司营业地址', es: 'Domicilio comercial del emisor' },
  { key: 'iibb', label: 'Ingresos Brutos', es: 'Ingresos Brutos' },
  { key: 'vat_condition', label: 'IVA 身份', es: 'Condición frente al IVA' },
  { key: 'inicio_actividad', label: '营业开始日期', es: 'Inicio de actividades' },
  { key: 'remito_document_code', label: '凭证类型代码', es: 'Código de comprobante (R = 91)' },
  { key: 'default_point_of_sale', label: '默认销售点', es: 'Punto de venta' },
  { key: 'cai', label: 'CAI 授权码', es: 'Código de Autorización de Impresión' },
  { key: 'cai_expires_at', label: 'CAI 有效期', es: 'Vencimiento del CAI' },
  { key: 'print_authorization_type', label: '打印授权方式', es: 'Régimen de autorización de impresión' },
];

// 授权 / 编号字段单独成块：属 ARCA 法定授权信息，务必以会计师在 ARCA（MF-1）确认的真实值填写。
const AUTH_FIELDS: CompanyField[] = [
  { key: 'remito_document_code', label: '凭证类型代码 Código comprobante', hint: 'Remito R = 91（ARCA RG 1415）' },
  { key: 'default_point_of_sale', label: '默认销售点 Punto de venta', hint: '4 位，留空则由交货单逐单指定' },
  { key: 'print_authorization_type', label: '打印授权方式', hint: '如 CAI / AFIP 预印 / 自印（须真实）' },
  { key: 'cai', label: 'CAI 授权码', hint: '14 位 · 由 ARCA 逐票授予，切勿臆造或用占位值伪装合规' },
  { key: 'cai_expires_at', label: 'CAI 有效期 Vencimiento', hint: 'ARCA 授予的到期日 · RG 5678/2025：电子图形表征不免除 CAI' },
];

const toText = (v: unknown): string => (v == null ? '' : String(v));

function strValue(p: CompanyProfile, key: keyof CompanyProfile): string {
  return toText((p as unknown as Record<string, unknown>)[key]);
}

export function CompanyProfilePage() {
  const { isAdmin } = useAuth();
  const { data, loading, error, reload } = useDetail<CompanyProfile>('/api/company');

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // 每次读到最新档案后回填编辑态（仅管理员会实际编辑）。
  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const f of [...COMPANY_FIELDS, ...AUTH_FIELDS]) next[f.key] = strValue(data, f.key);
    setDraft(next);
    setEnabled(data.remito_r_enabled === 1);
    setErr(null);
  }, [data]);

  const set = (k: string, v: string) => { setDraft((d) => ({ ...d, [k]: v })); setOk(null); };

  // 公司侧正式 Remito 字段缺失清单（仅信息展示，真正的门禁在交货单就绪检查 + 后端强制）。
  const companyMissing = useMemo(() => {
    if (!data) return [];
    return FISCAL_CHECKLIST.filter((c) => !strValue(data, c.key).trim());
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    for (const f of [...COMPANY_FIELDS, ...AUTH_FIELDS]) {
      if ((draft[f.key] ?? '') !== strValue(data, f.key)) return true;
    }
    if (enabled !== (data.remito_r_enabled === 1)) return true;
    return false;
  }, [data, draft, enabled]);

  const save = async () => {
    setErr(null); setOk(null);
    if (!draft.legal_name?.trim()) { setErr('公司法定名称不能为空'); return; }
    if (!data) return;
    // 仅提交发生变化的字段，避免误清空未编辑的真实值。
    const patch: Record<string, unknown> = {};
    for (const f of [...COMPANY_FIELDS, ...AUTH_FIELDS]) {
      const cur = draft[f.key] ?? '';
      if (cur !== strValue(data, f.key)) patch[f.key] = cur.trim() ? cur.trim() : null;
    }
    if (enabled !== (data.remito_r_enabled === 1)) patch.remito_r_enabled = enabled;
    if (!Object.keys(patch).length) { setErr('没有需要保存的改动'); return; }
    setBusy(true);
    try {
      await api.patch<{ data: CompanyProfile }>('/api/company', patch);
      setOk(`已保存 ${Object.keys(patch).length} 项并写入审计留痕。`);
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  const renderField = (f: CompanyField) => {
    const val = draft[f.key] ?? '';
    return (
      <Field key={f.key} label={f.label} hint={f.hint} required={f.key === 'legal_name'} span={f.span}>
        <TextInput
          value={val}
          readOnly={!isAdmin}
          placeholder={isAdmin ? '' : '资料待补'}
          onChange={(e) => set(f.key, e.target.value)}
        />
      </Field>
    );
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div className="detail-head" style={{ marginBottom: 12 }}>
        <h3 style={{ fontSize: 16 }}>公司财税资料 · Remito R 抬头</h3>
        {data ? <span className="muted" style={{ fontSize: 12.5 }}>{data.legal_name}</span> : null}
      </div>

      {loading && !data ? <div style={{ padding: 12 }}><span className="skeleton"><span className="spinner" />读取公司资料…</span></div> : null}
      {error ? <div className="notice bad" style={{ marginBottom: 12 }}>{error}</div> : null}
      {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
      {ok ? <div className="notice info" style={{ marginBottom: 12 }}>{ok}</div> : null}

      {!isAdmin ? (
        <div className="notice info" style={{ marginBottom: 'var(--gap)' }}>
          当前角色为只读查看。公司财税资料与正式 Remito 输出开关仅管理员可维护（后端 PATCH /api/company 强制校验）。
        </div>
      ) : null}

      <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head">
          <div><h2>抬头与联系</h2><div className="desc">GET /api/company · 打印抬头始终保留法定名称与税务数据文本，Logo 仅作可选图形</div></div>
        </div>
        <div className="panel-body">
          <FormGrid>{COMPANY_FIELDS.map(renderField)}</FormGrid>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head">
          <div>
            <h2>ARCA 授权与编号</h2>
            <div className="desc">凭证类型代码 / 销售点 / CAI 属法定授权信息 —— 请由会计师依据 ARCA（RG 1415、RG 5678/2025）核对后录入真实值</div>
          </div>
        </div>
        <div className="panel-body">
          <div className="notice warn" style={{ marginBottom: 12 }}>
            不得用占位值伪装合规。CAI 须由 ARCA 逐票授予；电子图形表征不免除 CAI 要求。留空的字段在正式输出就绪检查中会判为「资料待补」并阻止标记为正式 Remito。
          </div>
          <FormGrid>{AUTH_FIELDS.map(renderField)}</FormGrid>
        </div>
      </section>

      {data ? (
        <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
          <div className="panel-head">
            <div><h2>正式 Remito 输出总开关</h2><div className="desc">仅在会计师确认下列法定字段齐全后方可开启；开启后仍需逐单通过交货单「就绪检查」</div></div>
          </div>
          <div className="panel-body">
            <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 10, gridColumn: '1 / -1' }}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={!isAdmin}
                onChange={(e) => { setEnabled(e.target.checked); setOk(null); }}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>允许对外作为正式 Remito R 打印件</span>
            </label>
            {!isAdmin && data.remito_r_enabled === 1 ? (
              <div className="notice info" style={{ marginTop: 10 }}>当前总开关：已开启（正式输出启用中）。</div>
            ) : null}
            {enabled ? (
              <div className="notice warn" style={{ marginTop: 10 }}>
                开启即代表公司当前的 Remito R、销售点与 CAI 数据已由会计师确认为真实、可对外。请勿把普通 ERPNext 编号当作获批的法定 Remito 编号。
              </div>
            ) : null}

            <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>公司侧必备字段核对</div>
            {companyMissing.length === 0 ? (
              <div className="notice" style={{ background: 'var(--panel-inset)' }}>公司侧法定字段均已填写；正式输出仍需补齐交货单维度的销售点、连续编号、签发日期与收货方税务资料。</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {companyMissing.map((m) => (
                  <span key={m.key} className="pill warn" title={m.es}>缺：{m.label}</span>
                ))}
              </div>
            )}
          </div>
        </section>
      ) : null}

      <div className="detail-actions" style={{ marginTop: 4 }}>
        {isAdmin ? (
          <button className="btn primary" onClick={save} disabled={busy || !dirty}>{busy ? '保存中…' : '保存公司资料'}</button>
        ) : null}
        <button className="btn" onClick={reload} disabled={busy}>重新读取</button>
        {isAdmin && !dirty ? <span className="muted" style={{ fontSize: 11.5, marginLeft: 6 }}>暂无改动</span> : null}
        {data?.updated_at ? <span className="muted" style={{ fontSize: 11.5, marginLeft: 'auto' }}>最近更新：{dateLabel(data.updated_at)}</span> : null}
      </div>
    </div>
  );
}
